import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TableSortLabel,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  Alert,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { AxiosError } from 'axios';
import Header from '../common/Header';
import Sidebar from '../common/Sidebar';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { useHub } from '../../context/hub';
import { useTask } from '../../context/task';
import CreateTaskModal from './CreateTaskModal';
import { routingJobsApi } from '../../api/routingJobsApi';

interface TaskSummaryProps {
  onEditTask?: (taskId: string, depotId: string) => void;
  onVerifyTask?: (taskId: string, depotId: string) => void;
  onDepotConfigurationClick?: () => void;
}

interface JobFileEntry {
  name: string;
  storageId: string;
  source: 'input' | 'output';
}

type ArchiveDownloadType = 'all' | 'input' | 'output';

const RERUNNABLE_STATUSES = new Set(['CREATED', 'COMPLETED', 'FAILED']);
const EDITABLE_STATUSES = new Set(['COMPLETED', 'EDIT', 'TRIPS_CREATED']);
const VERIFIABLE_STATUS = 'DRAFT';
const TASK_LOOKBACK_MS = 3 * 24 * 60 * 60 * 1000;
const MIN_VISIBLE_TASKS = 10;

// Helper function to format date
const formatDate = (date: Date) => {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Status chip component with appropriate colors and icons
interface StatusChipProps {
  status: string;
}

const StatusChip: React.FC<StatusChipProps> = ({ status }) => {
  const statusConfig: Record<
    string,
    { color: 'success' | 'warning' | 'error' | 'default' | 'info' | 'primary' | 'secondary'; icon: React.ReactElement | null }
  > = {
    COMPLETED: { color: 'success', icon: null },
    TRIPS_CREATED: { color: 'secondary', icon: null },
    IN_PROGRESS: { color: 'info', icon: <HourglassEmptyIcon fontSize="small" /> },
    FAILED: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
    DRAFT: { color: 'warning', icon: <WarningIcon fontSize="small" /> },
    VERIFIED: { color: 'primary', icon: null },
    CREATED: { color: 'primary', icon: <HourglassEmptyIcon fontSize="small" /> },
    VERIFY: { color: 'warning', icon: <WarningIcon fontSize="small" /> },
    EDIT: { color: 'info', icon: <EditIcon fontSize="small" /> },
    SUBMITTED: { color: 'secondary', icon: null },
    'ALGO RUN': { color: 'primary', icon: <HourglassEmptyIcon fontSize="small" /> },
  };

  const normalized = (status || '').toString().toUpperCase();
  const config = statusConfig[normalized] || { color: 'default', icon: null };
  const label = normalized
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());

  return (
    <Chip 
      label={label}
      color={config.color}
      size="small"
      {...(config.icon ? { icon: config.icon } : {})}
      variant="outlined"
      sx={{ fontWeight: 'medium' }}
    />
  );
};

// Safe navigation function for use outside Router context
const useSafeNavigate = (): NavigateFunction | null => {
  try {
    return useNavigate();
  } catch (error) {
    console.warn('useNavigate hook is not available in this context');
    return null;
  }
};

const TaskSummary: React.FC<TaskSummaryProps> = ({
  onEditTask,
  onVerifyTask,
  onDepotConfigurationClick,
}) => {
  const { mode } = useCustomTheme();
  const { currentHubId, hubs } = useHub();
  const {
    tasks,
    loading,
    error,
    searchTerm,
    sortConfig,
    searchTasks,
    sortTasks,
    fetchTasks,
    refreshTask,
  } = useTask();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [rerunTaskId, setRerunTaskId] = useState<string | null>(null);
  const [refreshingTaskId, setRefreshingTaskId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadTaskContext, setDownloadTaskContext] = useState<{
    taskId: string;
    depotId: string;
    tenantId: string;
    modelType: string;
  } | null>(null);
  const [downloadFiles, setDownloadFiles] = useState<JobFileEntry[]>([]);
  const [downloadMenuLoading, setDownloadMenuLoading] = useState(false);
  const [downloadingFileStorageId, setDownloadingFileStorageId] = useState<string | null>(null);
  const [downloadingArchiveType, setDownloadingArchiveType] = useState<ArchiveDownloadType | null>(null);
  const [replacingFileStorageId, setReplacingFileStorageId] = useState<string | null>(null);

  // Get navigation function safely
  const navigateFunction = useSafeNavigate();
  
  // File upload modal state
  // Effect to fetch tasks when hubId changes
  useEffect(() => {
    if (currentHubId) {
      const timer = setTimeout(() => {
        fetchTasks(currentHubId);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentHubId, fetchTasks]);

  const currentHub = useMemo(
    () => hubs.find((hub) => hub.hubId === currentHubId),
    [hubs, currentHubId]
  );
  const filteredTasks = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return tasks;
    }

    return tasks.filter((task) => (
      task.taskId.toLowerCase().includes(normalizedSearchTerm) ||
      task.createdBy.toLowerCase().includes(normalizedSearchTerm) ||
      task.status.toLowerCase().includes(normalizedSearchTerm)
    ));
  }, [tasks, searchTerm]);

  const visibleTasks = useMemo(() => {
    const latestTasks = [...filteredTasks].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const cutoffTime = Date.now() - TASK_LOOKBACK_MS;
    const recentTasks = latestTasks.filter(
      (task) => new Date(task.createdAt).getTime() >= cutoffTime
    );
    const selectedTasks =
      recentTasks.length >= MIN_VISIBLE_TASKS
        ? recentTasks
        : latestTasks.slice(0, MIN_VISIBLE_TASKS);

    return [...selectedTasks].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue == null || bValue == null) {
        return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }

      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }

      return 0;
    });
  }, [filteredTasks, sortConfig]);

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchTasks(event.target.value);
  };
  

  // Handle edit task
  const handleEditTask = (taskId: string, depotId: string) => {
    if (onEditTask) {
      onEditTask(taskId, depotId);
    } else if (navigateFunction) {
      const targetHub = depotId || currentHubId || 'default';
      navigateFunction(`/edit-task/hub/${targetHub}/task/${taskId}`);
    }
  };
  
  // Handle verify task
  const handleVerifyTask = (taskId: string, depotId: string) => {
    if (onVerifyTask) {
      onVerifyTask(taskId, depotId);
    } else if (navigateFunction) {
      const targetHub = depotId || currentHubId || 'default';
      navigateFunction(`/verify-task/hub/${targetHub}/task/${taskId}`);
    }
  };

  const getPrimaryAction = (status: string) => {
    const normalizedStatus = (status || '').toUpperCase();
    if (EDITABLE_STATUSES.has(normalizedStatus)) {
      return 'edit' as const;
    }
    if (normalizedStatus === VERIFIABLE_STATUS) {
      return 'verify' as const;
    }
    return null;
  };

  // Handle UserInfo navigation
  const handleUserInfoClick = () => {
    if (navigateFunction && currentHubId) {
      navigateFunction(`/user-info/hub/${currentHubId}`);
    }
  };
  
  const handleUserManagementClick = () => {
    if (navigateFunction && currentHubId) {
      navigateFunction(`/user-management/hub/${currentHubId}`);
    }
  };

  const handleDepotConfigurationClick = () => {
    if (onDepotConfigurationClick) {
      onDepotConfigurationClick();
    }
  };

  const handleControlTowerClick = () => {
    if (navigateFunction) {
      navigateFunction('/control-tower');
    }
  };
  
  // Handle Route Plan click - just stays on the current page
  const handleRoutePlanClick = () => {
    if (navigateFunction && currentHubId) {
      navigateFunction(`/task-summary/hub/${currentHubId}`);
    }
  };

  const handleRerunTask = async (taskId: string, depotId?: string) => {
    if (!depotId) {
      setStatusMessage({ type: 'error', text: 'Unable to rerun because depot information is missing.' });
      return;
    }

    setStatusMessage(null);
    setRerunTaskId(taskId);

    try {
      await routingJobsApi.rerunJob(depotId, taskId);
      setStatusMessage({ type: 'success', text: `Algo rerun triggered for task ${taskId}.` });
      if (currentHubId) {
        fetchTasks(currentHubId);
      }
    } catch (err) {
      console.error('Failed to rerun task', err);
      let message = 'Failed to rerun task. Please try again.';
      if (err && typeof err === 'object') {
        const axiosError = err as AxiosError<{ message?: string }>;
        message = axiosError.response?.data?.message || axiosError.message || message;
      }
      setStatusMessage({ type: 'error', text: message });
    } finally {
      setRerunTaskId(null);
    }
  };

  const handleRefreshTask = async (taskId: string, depotId?: string) => {
    if (!depotId) {
      setStatusMessage({ type: 'error', text: 'Unable to refresh because depot information is missing.' });
      return;
    }

    setStatusMessage(null);
    setRefreshingTaskId(taskId);

    try {
      await refreshTask(depotId, taskId);
      setStatusMessage({ type: 'success', text: `Task ${taskId} refreshed.` });
    } catch (err) {
      console.error('Failed to refresh task', err);
      let message = 'Failed to refresh task. Please try again.';
      if (err && typeof err === 'object') {
        const axiosError = err as AxiosError<{ message?: string }>;
        message = axiosError.response?.data?.message || axiosError.message || message;
      }
      setStatusMessage({ type: 'error', text: message });
    } finally {
      setRefreshingTaskId(null);
    }
  };

  const parseFileMap = (raw: string | undefined): Record<string, string> => {
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }
      const normalized: Record<string, string> = {};
      Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
        if (value == null) return;
        const storageId = String(value).trim();
        if (storageId) {
          normalized[key] = storageId;
        }
      });
      return normalized;
    } catch {
      return {};
    }
  };

  const toDownloadFileName = (taskId: string, source: 'input' | 'output', name: string): string => {
    const sanitize = (value: string) => value.replace(/[^a-zA-Z0-9._-]/g, '_');
    const normalizedName = sanitize(name).endsWith('.csv') ? sanitize(name) : `${sanitize(name)}.csv`;
    return `${sanitize(taskId)}-${source}-${normalizedName}`;
  };

  const toArchiveDownloadFileName = (taskId: string, type: ArchiveDownloadType): string => {
    const sanitize = (value: string) => value.replace(/[^a-zA-Z0-9._-]/g, '_');
    return `${sanitize(taskId)}-${type}-files.zip`;
  };

  const toArchiveEntryFileName = (file: JobFileEntry, includeSourceDirectory: boolean): string => {
    const sanitizeSegment = (value: string) => value.replace(/[^a-zA-Z0-9._-]/g, '_');
    const sanitizedName = sanitizeSegment(file.name);
    const fileName = sanitizedName.endsWith('.csv') ? sanitizedName : `${sanitizedName}.csv`;
    return includeSourceDirectory ? `${file.source}/${fileName}` : fileName;
  };

  const triggerBrowserDownload = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const handleOpenDownloadMenu = async (
    event: React.MouseEvent<HTMLElement>,
    taskId: string,
    depotId?: string
  ) => {
    event.stopPropagation();
    if (!depotId) {
      setStatusMessage({ type: 'error', text: 'Unable to download files because depot information is missing.' });
      return;
    }
    setDownloadAnchorEl(event.currentTarget);
    setDownloadTaskContext(null);
    setDownloadFiles([]);
    setDownloadMenuLoading(true);
    try {
      const job = await routingJobsApi.getJob(depotId, taskId);
      const tenantId = job.depotSnapshot?.tenantId;
      const modelType = job.depotSnapshot?.modelType;
      if (!tenantId || !modelType) {
        throw new Error('Tenant metadata is missing for this task');
      }
      setDownloadTaskContext({ taskId, depotId, tenantId, modelType });
      const inputFiles = parseFileMap(job.inputFilesMap);
      const outputFiles = parseFileMap(job.outputFilesMap);
      const files: JobFileEntry[] = [
        ...Object.entries(inputFiles).map(([name, storageId]) => ({ name, storageId, source: 'input' as const })),
        ...Object.entries(outputFiles).map(([name, storageId]) => ({ name, storageId, source: 'output' as const })),
      ].sort((a, b) => {
        if (a.source !== b.source) return a.source.localeCompare(b.source);
        return a.name.localeCompare(b.name);
      });
      setDownloadFiles(files);
    } catch (err) {
      console.error('Failed to fetch task files for download', err);
      setStatusMessage({ type: 'error', text: `Failed to fetch files for task ${taskId}.` });
      setDownloadFiles([]);
    } finally {
      setDownloadMenuLoading(false);
    }
  };

  const handleCloseDownloadMenu = () => {
    if (downloadingArchiveType || downloadingFileStorageId || replacingFileStorageId) return;
    setDownloadAnchorEl(null);
    setDownloadTaskContext(null);
    setDownloadFiles([]);
    setDownloadMenuLoading(false);
  };

  const handleDownloadFile = async (file: JobFileEntry) => {
    if (!downloadTaskContext) return;
    setDownloadingFileStorageId(file.storageId);
    try {
      const blob = await routingJobsApi.downloadInternalFile(file.storageId);
      const fileName = toDownloadFileName(downloadTaskContext.taskId, file.source, file.name);
      triggerBrowserDownload(blob, fileName);
    } catch (err) {
      console.error('Failed to download task file', err);
      setStatusMessage({ type: 'error', text: `Failed to download file ${file.name}.` });
    } finally {
      setDownloadingFileStorageId(null);
    }
  };

  const handleReplaceFile = async (
    targetFile: JobFileEntry,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !downloadTaskContext) return;

    setReplacingFileStorageId(targetFile.storageId);
    try {
      // This endpoint enforces tenant/model/fileType validators before storing the replacement.
      const uploaded = await routingJobsApi.uploadTenantFile(
        downloadTaskContext.tenantId,
        downloadTaskContext.modelType,
        targetFile.name,
        file
      );
      await routingJobsApi.replaceJobFile(downloadTaskContext.depotId, downloadTaskContext.taskId, {
        fileType: targetFile.name,
        storageId: uploaded.storageId,
        source: targetFile.source,
      });
      setDownloadFiles((prev) =>
        prev.map((entry) =>
          entry.storageId === targetFile.storageId && entry.name === targetFile.name && entry.source === targetFile.source
            ? { ...entry, storageId: uploaded.storageId }
            : entry
        )
      );
      setStatusMessage({ type: 'success', text: `Replaced ${targetFile.source}:${targetFile.name}` });
      if (currentHubId) {
        fetchTasks(currentHubId);
      }
    } catch (err) {
      console.error('Failed to replace task file', err);
      const axiosError = err as AxiosError<{ message?: string }>;
      setStatusMessage({
        type: 'error',
        text: axiosError.response?.data?.message || axiosError.message || `Failed to replace file ${targetFile.name}.`,
      });
    } finally {
      setReplacingFileStorageId(null);
    }
  };

  const handleDownloadArchive = async (type: ArchiveDownloadType) => {
    if (!downloadTaskContext) return;
    const filesToArchive = downloadFiles.filter((file) => type === 'all' || file.source === type);
    if (filesToArchive.length === 0) return;

    setDownloadingArchiveType(type);
    try {
      const blob = await routingJobsApi.downloadInternalFilesArchive({
        archiveName: toArchiveDownloadFileName(downloadTaskContext.taskId, type),
        files: filesToArchive.map((file) => ({
          fileId: file.storageId,
          fileName: toArchiveEntryFileName(file, type === 'all'),
        })),
      });
      triggerBrowserDownload(blob, toArchiveDownloadFileName(downloadTaskContext.taskId, type));
      setStatusMessage({ type: 'success', text: `Downloaded ${type} zip for task ${downloadTaskContext.taskId}.` });
    } catch (err) {
      console.error(`Failed to download ${type} zip for task`, err);
      setStatusMessage({ type: 'error', text: `Failed to download ${type} zip for this task.` });
    } finally {
      setDownloadingArchiveType(null);
    }
  };

  const inputDownloadFiles = downloadFiles.filter((file) => file.source === 'input');
  const outputDownloadFiles = downloadFiles.filter((file) => file.source === 'output');

  useEffect(() => {
    if (!statusMessage) {
      return;
    }
    const timer = setTimeout(() => setStatusMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
    
      <Header
        taskInfo={null}
        loading={false}
        isTaskSummary={true}
        showMetrics={false}
      />
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <Sidebar
          taskInfo={null}
          loading={loading}
          selectedTrips={[]}
          onTripSelect={() => {}}
          onTripVisibilityToggle={() => {}}
          searchTerm=""
          onSearch={() => {}}
          onToggleMapOption={() => {}}
          showPolygons={true}
          showSequence={true}
          onUserInfoClick={handleUserInfoClick}
          onRoutePlanClick={handleRoutePlanClick}
          onUserManagementClick={handleUserManagementClick}
          onDepotConfigurationClick={handleDepotConfigurationClick}
          onControlTowerClick={handleControlTowerClick}
          activeItem="routePlans"
        />
        <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto', bgcolor: mode === 'dark' ? 'grey.900' : 'grey.100' }}>
          {/* Search and filters */}
          <Paper sx={{ p: 2, mb: 0, borderRadius: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  placeholder="Search by ID, creator, or status"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearch}
                  sx={{ width: 300 }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>) }}
                />
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  {visibleTasks.length} tasks found
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={!currentHubId || !currentHub}
                onClick={() => setCreateModalOpen(true)}
              >
                Create Draft Task
              </Button>
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {statusMessage && (
            <Alert severity={statusMessage.type} sx={{ mb: 3 }} onClose={() => setStatusMessage(null)}>
              {statusMessage.text}
            </Alert>
          )}

          {/* Tasks table */}
          <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
            <Table sx={{ minWidth: 650 }} size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel onClick={() => sortTasks('taskId')}>
                      Task ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel onClick={() => sortTasks('status')}>
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel onClick={() => sortTasks('createdBy')}>
                      Created By
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel onClick={() => sortTasks('createdAt')}>
                      Created At
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel onClick={() => sortTasks('updatedAt')}>
                      Updated At
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={32} />
                      <Typography sx={{ mt: 2 }} color="text.secondary">
                        Loading tasks...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : visibleTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {searchTerm ? 'No tasks match your search' : 'No tasks found for this depot'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleTasks.map((task, index) => {
                    const normalizedStatus = (task.status || '').toUpperCase();
                    const isRerunInProgress = rerunTaskId === task.taskId;
                    const canRerun = RERUNNABLE_STATUSES.has(normalizedStatus);
                    const primaryAction = getPrimaryAction(task.status);
                    return (
                      <TableRow
                        key={task.taskId}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: index % 2 === 0 ? (mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)') : 'transparent' }}
                      >
                        <TableCell component="th" scope="row">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title="Refresh task" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRefreshTask(task.taskId, task.depotId);
                                  }}
                                  disabled={refreshingTaskId === task.taskId}
                                >
                                  {refreshingTaskId === task.taskId ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <RefreshIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Typography variant="body2" fontWeight="medium">
                              {task.taskId}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell><StatusChip status={task.status} /></TableCell>
                        <TableCell>{task.createdBy}</TableCell>
                        <TableCell>{formatDate(task.createdAt)}</TableCell>
                        <TableCell>{formatDate(task.updatedAt)}</TableCell>
                        <TableCell>
                          {task.metrics && Object.keys(task.metrics).length > 0 ? (
                            <Stack spacing={0.25} alignItems="flex-start">
                              {Object.entries(task.metrics)
                                .slice(0, 2)
                                .map(([key, value]) => (
                                  <Typography key={key} variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                                    {key}: {value}
                                  </Typography>
                                ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {primaryAction === 'verify' && (
                              <Button size="small" color="warning" onClick={(event) => { event.stopPropagation(); handleVerifyTask(task.taskId, task.depotId); }}>
                                Verify
                              </Button>
                            )}
                            {primaryAction === 'edit' && (
                              <Button size="small" color="primary" onClick={(event) => { event.stopPropagation(); handleEditTask(task.taskId, task.depotId); }}>
                                VIEW
                              </Button>
                            )}
                            <Button
                              size="small"
                              color="secondary"
                              onClick={(event) => {
                                event.stopPropagation();
                                if (!canRerun) {
                                  return;
                                }
                                handleRerunTask(task.taskId, task.depotId);
                              }}
                              disabled={!canRerun || isRerunInProgress}
                              startIcon={
                                isRerunInProgress ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : (
                                  <AutorenewIcon fontSize="small" />
                                )
                              }
                            >
                              Rerun
                            </Button>
                            <Button
                              size="small"
                              color="inherit"
                              startIcon={<DownloadIcon fontSize="small" />}
                              onClick={(event) => {
                                handleOpenDownloadMenu(event, task.taskId, task.depotId);
                              }}
                            >
                              Download
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

        </Box>
      </Box>
      <Menu
        anchorEl={downloadAnchorEl}
        open={Boolean(downloadAnchorEl)}
        onClose={handleCloseDownloadMenu}
      >
        <MenuItem
          onClick={() => handleDownloadArchive('all')}
          disabled={downloadMenuLoading || Boolean(downloadingArchiveType) || downloadFiles.length === 0 || Boolean(replacingFileStorageId)}
        >
          {downloadingArchiveType === 'all' ? 'Downloading all zip...' : `Download all zip (${downloadFiles.length})`}
        </MenuItem>
        <MenuItem
          onClick={() => handleDownloadArchive('input')}
          disabled={downloadMenuLoading || Boolean(downloadingArchiveType) || inputDownloadFiles.length === 0 || Boolean(replacingFileStorageId)}
        >
          {downloadingArchiveType === 'input' ? 'Downloading input zip...' : `Download input zip (${inputDownloadFiles.length})`}
        </MenuItem>
        <MenuItem
          onClick={() => handleDownloadArchive('output')}
          disabled={downloadMenuLoading || Boolean(downloadingArchiveType) || outputDownloadFiles.length === 0 || Boolean(replacingFileStorageId)}
        >
          {downloadingArchiveType === 'output' ? 'Downloading output zip...' : `Download output zip (${outputDownloadFiles.length})`}
        </MenuItem>
        <Divider />
        {downloadMenuLoading && (
          <MenuItem disabled>Loading files...</MenuItem>
        )}
        {!downloadMenuLoading && downloadFiles.length === 0 && (
          <MenuItem disabled>No files found for this task</MenuItem>
        )}
        {!downloadMenuLoading &&
          downloadFiles.map((file) => (
            <MenuItem
              key={`${file.source}:${file.name}:${file.storageId}`}
              disabled={Boolean(downloadingFileStorageId) || Boolean(downloadingArchiveType) || Boolean(replacingFileStorageId)}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: file.source === 'input' ? 'primary.main' : 'secondary.main' }}>
                    {file.source.toUpperCase()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDownloadFile(file);
                    }}
                  >
                    {file.name}
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  component="label"
                  onClick={(event) => event.stopPropagation()}
                  disabled={Boolean(downloadingFileStorageId) || Boolean(downloadingArchiveType) || Boolean(replacingFileStorageId)}
                >
                  {replacingFileStorageId === file.storageId ? 'Replacing...' : 'Replace'}
                  <input
                    hidden
                    type="file"
                    accept=".csv,text/csv"
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      event.stopPropagation();
                      handleReplaceFile(file, event);
                    }}
                  />
                </Button>
              </Stack>
            </MenuItem>
          ))}
      </Menu>
      <CreateTaskModal
        open={createModalOpen}
        depotId={currentHubId}
        tenantId={currentHub?.tenantId}
        modelType={currentHub?.modelType}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(task) => {
          handleVerifyTask(task.id, task.depotId);
        }}
      />
    </Box>
  );
};

export default TaskSummary; 
